// Import React and hooks for state, effect, memoization, and references
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";

// Import FontAwesome icons for social sharing
import { faTwitter, faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Custom hooks for fetching comments and adding a new comment
import useComments from "../hooks/useComments";
import useAddComment from "../hooks/useAddComment";

// NewsModal component renders the full news article in a modal with sharing and comments
const NewsModal = ({ show, handleClose, article, category }) => {
  // State to hold cleaned and formatted full content of the article
  const [fullContent, setFullContent] = useState("Loading full news content...");
  const modalRef = useRef(null); // Reference to modal for scrolling and focus

  // State for commenter's name and text input, initializing name from localStorage if exists
  const [name, setName] = useState(() => localStorage.getItem("commenterName") || "");
  const [text, setText] = useState("");

  // Unique identifier for the article
  const newsId = article?.link || "";
  // Fetch comments for this article using custom hook
  const { comments } = useComments(newsId);
  // Hook to post new comment
  const addComment = useAddComment();

  // Handle comment form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return; // Ignore empty comments

    // Validate name, default to "Anonymous" if too short
    const commenterName = name.trim().length >= 2 ? name.trim() : "Anonymous";

    // Store valid name in localStorage to remember on this device
    if (name.trim().length >= 2) {
      localStorage.setItem("commenterName", name.trim());
    }

    // Add comment to backend or Firestore
    await addComment({ newsId, text, name: commenterName });
    setText(""); // Clear comment input
    setName(""); // Clear name input
  };

  // Phrases or patterns to remove from article content
  const UNWANTED_PHRASES = useMemo(() => [
    "READ ALSO:", "READ ALSO", "READ:", "READ", "The post appeared first on ChannelsTV",
    "The post .* appeared first on .*", "Watch the interview below:", "All rights reserved", "Copyright ©",
    "Follow us on", "Share this article", "Related News", "For more information",
    "Contact us at", "Like our Facebook page", "Follow us on Twitter", "Subscribe to our channel",
    "Join our Telegram", "Download our app", "Kindly share this story", "Watch the video below:",
    "punchng.com", "vanguardngr.com", "channelstv.com", "thisdaylive.com", "© \\d{4}",
    /EU Slaps .* Fines On .*/i, /EU Exports To US Surge By .*/i
  ], []);

  // Function to clean article paragraphs: remove unwanted phrases, links, images, HTML tags
  const cleanContent = useCallback((paragraphs) => {
    return paragraphs
      .map(paragraph => {
        let cleaned = paragraph;

        // Remove each unwanted phrase
        UNWANTED_PHRASES.forEach(phrase => {
          if (typeof phrase === "string") {
            cleaned = cleaned.replace(new RegExp(phrase, "gi"), "");
          } else if (phrase instanceof RegExp) {
            cleaned = cleaned.replace(phrase, "");
          }
        });

        // Remove links, images, formatting tags, extra spaces
        cleaned = cleaned
          .replace(/<a\b[^>]*>(.*?)<\/a>/gi, "")
          .replace(/<img\b[^>]*>/gi, "")
          .replace(/<strong>(.*?)<\/strong>/gi, "$1")
          .replace(/<em>(.*?)<\/em>/gi, "$1")
          .replace(/&nbsp;/g, " ")
          .replace(/\s+/g, " ")
          .trim();

        return cleaned;
      })
      .filter(paragraph => {
        const textContent = paragraph.replace(/<[^>]+>/g, "").trim();
        // Keep paragraphs with meaningful text (>20 chars) and remove symbols/numbers-only paragraphs
        return textContent.length > 20 &&
          !/^[\s\W]*$/.test(textContent) &&
          !/^[\d\W]+$/.test(textContent);
      });
  }, [UNWANTED_PHRASES]);

  // Effect to process and set full content when modal is shown
  useEffect(() => {
    if (show && article?.contentParagraphs?.length > 0) {
      const cleaned = cleanContent(article.contentParagraphs);

      // Remove last paragraph if it contains source/credit info
      if (cleaned.length > 0) {
        const last = cleaned[cleaned.length - 1];
        if (/source:|credit:|via |©|all rights reserved/i.test(last)) {
          cleaned.pop();
        }
      }

      // Wrap each paragraph in <p> tags
      let htmlParagraphs = cleaned.map(p => `<p class="mb-4">${p}</p>`);

      // Optional: insert ad placeholder after 2 paragraphs
      if (htmlParagraphs.length > 2) {
        htmlParagraphs.splice(
          2,
          0,
           // `<div class="my-6 bg-gray-100 border border-gray-300 rounded p-4 text-center">
          //   <span class="text-sm text-gray-500">Propeller Ad</span>
          //   <div class="mt-2">
          //     <img src="https://placehold.co/728x90?text=PROPELLER+AD" alt="Propeller Ad" class="mx-auto" />
          //   </div>
          // </div>`
        );
      }

      const filteredContent = htmlParagraphs.join("");
      setFullContent(filteredContent || "<p>Content not available.</p>");
    } else {
      setFullContent("<p>Content not available.</p>");
    }
  }, [show, article, cleanContent]);

  // Effect to handle modal scrolling and disable body scroll
  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden"; // Prevent background scroll
      setTimeout(() => {
        modalRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100); // Smooth scroll to top of modal
    } else {
      document.body.style.overflow = ""; // Restore scroll when modal closes
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [show]);

  // Prepare share text for Twitter/WhatsApp
  const frontendUrl = `https://www.newstarn.com/${category || "general"}`;
  const sourceUrl = article?.link || "";
  const title = article?.title || "";
  const shareText = `${title}\n\nRead on Newstarn: ${frontendUrl}\n\nOriginal Source: ${sourceUrl}`;

  if (!show) return null; // Do not render modal if `show` is false

  return (
    <div
      id="news-modal-backdrop"
      onClick={(e) => {
        if (e.target.id === "news-modal-backdrop") handleClose(); // Close modal on backdrop click
      }}
      className="fixed inset-0 z-50 flex justify-center bg-black bg-opacity-60 p-2 overflow-y-auto"
    >
      <div
        ref={modalRef}
        className="relative w-full max-w-3xl bg-white rounded-lg shadow-lg max-h-[90vh] overflow-y-auto"
      >
        {/* Header with title and close button */}
        <div className="flex justify-between items-center px-6 py-4 border-b bg-white">
          <h2 className="text-lg md:text-xl font-semibold">{title}</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-red-500 text-2xl font-bold leading-none"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 text-gray-800">
          {/* Optional main image */}
          {article?.image && (
            <div className="text-center mb-4">
              <img
                src={article.image}
                alt="News"
                className="w-full object-cover rounded"
              />
            </div>
          )}

          {/* Render full cleaned content */}
          <div
            className="prose max-w-none leading-relaxed"
            dangerouslySetInnerHTML={{ __html: fullContent }}
          />

            {/* Advertisement */}
          {/* <div className="my-6 bg-gray-100 border border-gray-300 rounded p-4 text-center">
            <span className="text-sm text-gray-500">Advertisement</span>
            <div className="mt-2">
              <img
                src="https://placehold.co/728x90?text=AD+BANNER"
                alt="Ad banner"
                className="mx-auto"
              />
            </div>
          </div> */}

          {/* Social sharing buttons */}
          <div className="mt-6 flex flex-wrap gap-3 items-center">
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm"
            >
              <FontAwesomeIcon icon={faTwitter} /> Share on X
            </a>
            <a
              href={`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded text-sm"
            >
              <FontAwesomeIcon icon={faWhatsapp} /> Share on WhatsApp
            </a>
          </div>

          {/* Comment section */}
          <div className="mt-10 border-t pt-6">
            <h3 className="text-lg font-semibold mb-2">💬 Leave a Comment</h3>
            <p className="text-sm text-gray-500 mb-4">
              Your name will be remembered on this device unless you're in Incognito or clear your browser history.
            </p>

            {/* Comment form */}
            <form onSubmit={handleSubmit} className="mb-6">
              <input
                type="text"
                className="w-full mb-3 p-2 border rounded"
                placeholder="Your name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                minLength={2}
              />
              <textarea
                id="message"
                className="w-full mb-3 p-2 border rounded"
                placeholder="Write your comment..."
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={3}
                required
              />
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm"
              >
                Post Comment
              </button>
            </form>

            {/* List of existing comments */}
            {comments.length > 0 ? (
              <ul className="space-y-4">
                {comments.map((c) => (
                  <li key={c.id} className="border rounded p-3 bg-gray-50">
                    <div className="text-sm font-medium text-gray-800">
                      {c.name || "Anonymous"}
                    </div>
                    <div className="text-gray-700 text-sm">
                      {c.text}
                      {!c.moderated && (
                        <span className="ml-2 text-xs text-orange-500 font-semibold">(Under Moderation)</span>
                      )}
                    </div>
                    {c.timestamp?.seconds && (
                      <div className="text-xs text-gray-400 mt-1">
                        {new Date(c.timestamp.seconds * 1000).toLocaleString()}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No comments yet. Be the first!</p>
            )}
          </div>

          {/* Spacer at the bottom */}
          <div className="h-12" />
        </div>
      </div>
    </div>
  );
};

// Export component for usage in NewsItem or other pages
export default NewsModal;
